import { Screen, useApp, useTranslatedTag, useTranslation, useUserSettings } from "@next-platform/client-common-core";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import buildRulebookBotsFilter from "@next-platform/client-common-core/rulebook/profile/buildRulebookBotsFilter";
import { FetchStatus } from "@next-platform/client-common-core/user/userSettings/types";
import { CharacterCardActions } from "@next-platform/client-common-spicy-ui";
import type { TFunction } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import InstantSearchAdapter, { type TypesenseInstantsearchAdapterOptions } from "typesense-instantsearch-adapter";

import useShouldFilterNsfwCharacters from "@/components/search/hooks/useShouldFilterNsfwCharacters";
import type { CharacterCardDropdownActions, SearchAdapter } from "@/components/search/types";
import { createSearchClientConfig } from "@/components/search/utils/createSearchClientConfig";
import getHitCharacterConfig from "@/components/search/utils/getHitCharacterConfig";
import {
  DEFAULT_INCLUDE_FIELDS_FOR_SEARCH_CLIENT,
  DEFAULT_QUERY_BY_FOR_SEARCH_CLIENT,
  NSFW_EXCLUSION_FILTER,
  NSFW_TOGGLE_LOCALSTORAGE_KEY,
  NUM_MESSAGES_24H_DESC_SORT,
} from "@/constants";
import { useAgeVerificationFlags } from "@/hooks";
import { useLocalizedPathname } from "@/hooks/useLocalizedPathname";
import useAuth from "@/services/useAuth";
import { Collection, TypesenseApiKey, TypesenseCollectionName } from "@/types/api";
import type { HitConfig, HitItem } from "@/types/hits";
import { getBusinessRouteScreen } from "@/utils";
import { getIsPartner } from "@/utils/getIsPartner";
import { typedLocalStorageService } from "@/utils/localStorageRe";

export const PROFILE_BOTS_INITIAL_PAGE_SIZE = 3;
export const PROFILE_BOTS_EXPANDED_PAGE_SIZE = 12;

export enum ProfileBotsStatus {
  IDLE = "idle",
  LOADING = "loading",
  ERROR = "error",
  READY = "ready",
}

type ProfileBotsState =
  | { status: ProfileBotsStatus.IDLE | ProfileBotsStatus.LOADING }
  | { status: ProfileBotsStatus.ERROR }
  | { status: ProfileBotsStatus.READY; hits: HitItem[]; found: number; hasMore: boolean };
/** The slice of `SearchResponse<HitItem>` (typesense SDK) this hook consumes. */
type ProfileBotsSearchResponse = { hits?: { document: HitItem }[]; found?: number } | undefined;

const hitKey = (hit: HitItem): string => String(hit.character_id ?? hit.id);

/** Typesense multi_search on the public characters collection — the proxy only exposes POST /multi_search. */
const searchCharactersByRulebook = async ({
  adapter,
  collectionName,
  rulebookId,
  applicationId,
  perPage,
  page,
  shouldFilterNsfw,
}: {
  adapter: SearchAdapter;
  collectionName: string;
  rulebookId: string;
  applicationId: string;
  perPage: number;
  page: number;
  shouldFilterNsfw: boolean;
}): Promise<ProfileBotsSearchResponse> => {
  const response = await adapter.typesenseClient.multiSearch.perform<[HitItem]>({
    searches: [
      {
        collection: collectionName,
        q: "*",
        query_by: DEFAULT_QUERY_BY_FOR_SEARCH_CLIENT,
        // Same SFW exclusion the explore filter chain uses (isNsfwCharacter covers flag + tags).
        filter_by: buildRulebookBotsFilter({
          rulebookId,
          applicationId,
          nsfwExclusionFilter: shouldFilterNsfw ? NSFW_EXCLUSION_FILTER : undefined,
        }),
        include_fields: DEFAULT_INCLUDE_FIELDS_FOR_SEARCH_CLIENT,
        sort_by: NUM_MESSAGES_24H_DESC_SORT,
        per_page: perPage,
        page,
      },
    ],
  });
  const result = response.results[0] as ProfileBotsSearchResponse & { error?: string; code?: number };
  // Per-search failures come back inside HTTP 200 results — surface them like a rejected request.
  if (result?.error) throw new Error(result.error);
  return result;
};

const readyResultOf = (response: ProfileBotsSearchResponse, perPage: number): ProfileBotsState => {
  const documents = (response?.hits ?? []).map((hit) => hit.document);
  const found = response?.found ?? documents.length;
  return { status: ProfileBotsStatus.READY, hits: documents, found, hasMore: documents.length === perPage && documents.length < found };
};

const mergeHitPage = (previous: ProfileBotsState, response: ProfileBotsSearchResponse): ProfileBotsState => {
  if (previous.status !== ProfileBotsStatus.READY) {
    return previous;
  }
  const documents = (response?.hits ?? []).map((hit) => hit.document);
  const seen = new Set(previous.hits.map(hitKey));
  const merged = [...previous.hits, ...documents.filter((hit) => !seen.has(hitKey(hit)))];
  return { ...previous, hits: merged, hasMore: merged.length < (response?.found ?? previous.found) };
};

type HitConfigContext = {
  likedBots: ReturnType<typeof useUserSettings>["likedBots"];
  chatLanguage: string;
  localizePathname: (path: string) => string;
  t: TFunction;
  getTranslatedTag: ReturnType<typeof useTranslatedTag>["getTranslatedTag"];
  blurNSFW: boolean;
  isPartner: boolean;
  isAgeVerificationRequired: boolean;
  isAuthenticated: boolean;
  characterCardActionsHandler: (action: CharacterCardActions | CharacterCardDropdownActions, characterId: string) => void;
};

const buildCardConfig = (hit: HitItem, context: HitConfigContext): HitConfig => {
  const config = getHitCharacterConfig({
    character: hit,
    likedBots: context.likedBots,
    chatLanguage: context.chatLanguage,
    dislikedRecommendedCharacterIds: [],
    localizePathname: context.localizePathname,
    t: context.t,
    translateTag: context.getTranslatedTag,
    blurNSFW: context.blurNSFW,
    isPartner: context.isPartner,
    isAgeVerificationRequired: context.isAgeVerificationRequired,
    isAuthenticated: context.isAuthenticated,
    characterCardActionsHandler: context.characterCardActionsHandler,
  });
  // The config's `isHidden` is a recommId string on the loose hit shape — HitConfig wants a boolean.
  return { ...config, isHidden: Boolean(config.isHidden), dropdownActions: undefined };
};

/** The Typesense adapter instance for the public characters collection (built once the api key arrives). */
const useCharactersAdapter = (apiKey: string | undefined): SearchAdapter | null => {
  const [stateAdapter, setStateAdapter] = useState<SearchAdapter | null>(null);
  useEffect(() => {
    if (!apiKey) {
      return;
    }
    const config = createSearchClientConfig({
      apiKey,
      queryBy: DEFAULT_QUERY_BY_FOR_SEARCH_CLIENT,
      includeFields: DEFAULT_INCLUDE_FIELDS_FOR_SEARCH_CLIENT,
    });
    setStateAdapter(new InstantSearchAdapter(config as TypesenseInstantsearchAdapterOptions) as SearchAdapter);
  }, [apiKey]);
  return stateAdapter;
};

/** Paged query state: first page (3 or 12 after "See all") plus expanded load-more merging. */
const useProfileBotsQuery = ({
  rulebookId,
  adapter,
  collectionName,
  applicationId,
  isEnabled,
  shouldFilterNsfw,
}: {
  rulebookId: string;
  adapter: SearchAdapter | null;
  collectionName: string;
  applicationId: string;
  isEnabled: boolean;
  shouldFilterNsfw: boolean;
}) => {
  const [stateResult, setStateResult] = useState<ProfileBotsState>({ status: ProfileBotsStatus.IDLE });
  const [stateIsExpanded, setStateIsExpanded] = useState(false);
  const [stateIsLoadingMore, setStateIsLoadingMore] = useState(false);
  const refRequest = useRef(0);
  const refFetchedKey = useRef<string | null>(null);
  const refFailedKey = useRef<string | null>(null);
  const perPage = stateIsExpanded ? PROFILE_BOTS_EXPANDED_PAGE_SIZE : PROFILE_BOTS_INITIAL_PAGE_SIZE;
  const queryKey = `${rulebookId}|${collectionName}|${applicationId}|${shouldFilterNsfw}`;

  // A failed expand keeps the last ready page; error only when nothing loaded. No retry until the queryKey changes.
  const handleSearchFailure = useCallback(
    ({ request, isRefetch }: { request: number; isRefetch: boolean }) => {
      if (request !== refRequest.current) return;
      if (!isRefetch) refFailedKey.current = queryKey;
      setStateResult((previous) => (previous.status === ProfileBotsStatus.READY ? previous : { status: ProfileBotsStatus.ERROR }));
      setStateIsExpanded(false);
    },
    [queryKey],
  );

  useEffect(() => {
    if (!adapter || !collectionName || !isEnabled || refFailedKey.current === queryKey) {
      return;
    }
    const request = ++refRequest.current;
    const isRefetch = refFetchedKey.current === queryKey;
    if (isRefetch) {
      // Same query, page-size refetch ("See all"): keep the cards — the buttons show the loading state instead.
      setStateIsLoadingMore(true);
      setStateResult((previous) => (previous.status === ProfileBotsStatus.READY ? previous : { status: ProfileBotsStatus.LOADING }));
    } else {
      // New query: drop the previous rulebook's results entirely. When expanded, skip this run —
      // collapsing to the first page size re-fires the effect and fetches at the initial size.
      refFetchedKey.current = queryKey;
      refFailedKey.current = null;
      setStateIsExpanded(false);
      setStateIsLoadingMore(false);
      setStateResult({ status: ProfileBotsStatus.LOADING });
      if (stateIsExpanded) {
        return;
      }
    }
    searchCharactersByRulebook({ adapter, collectionName, rulebookId, applicationId, perPage, page: 1, shouldFilterNsfw })
      .then((response) => {
        if (request === refRequest.current) {
          setStateResult(readyResultOf(response, perPage));
        }
      })
      .catch(() => {
        handleSearchFailure({ request, isRefetch });
      })
      .finally(() => {
        if (request === refRequest.current) {
          setStateIsLoadingMore(false);
        }
      });
  }, [adapter, collectionName, rulebookId, applicationId, isEnabled, perPage, shouldFilterNsfw, queryKey, stateIsExpanded, handleSearchFailure]);

  const handleSeeAll = useCallback(() => {
    setStateIsExpanded(true);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (stateResult.status !== ProfileBotsStatus.READY || adapter === null) {
      return;
    }
    const nextPage = Math.floor(stateResult.hits.length / PROFILE_BOTS_EXPANDED_PAGE_SIZE) + 1;
    const request = ++refRequest.current;
    setStateIsLoadingMore(true);
    const finish = () => {
      if (request === refRequest.current) {
        setStateIsLoadingMore(false);
      }
    };
    searchCharactersByRulebook({
      adapter,
      collectionName,
      rulebookId,
      applicationId,
      perPage: PROFILE_BOTS_EXPANDED_PAGE_SIZE,
      page: nextPage,
      shouldFilterNsfw,
    })
      .then((response) => {
        if (request === refRequest.current) {
          setStateResult(mergeHitPage(stateResult, response));
        }
      })
      .catch(() => {})
      .finally(finish);
  }, [stateResult, adapter, collectionName, rulebookId, applicationId, shouldFilterNsfw]);

  return { stateResult, stateIsExpanded, stateIsLoadingMore, handleSeeAll, handleLoadMore };
};

/**
 * "Bots using it": paged public-Characters-Typesense hits filtered on `rulebook_id`. Starts at 3
 * cards; "See all" expands to 12-per-page load-more. Failures stay silent — the section hides.
 */
export default function useRulebookProfileBots({ rulebookId }: { rulebookId: string }) {
  const { typesenseConfig } = useApp();
  const { isAuthenticated } = useAuth();
  const { showNSFW, likedBots, blurNSFW, chatLanguage, onUserLikebot, areUserSettingsLoaded, areUserSettingLoading } = useUserSettings();
  const { isAgeVerificationRequired } = useAgeVerificationFlags();
  const { t } = useTranslation();
  const { getTranslatedTag } = useTranslatedTag();
  const localizePathname = useLocalizedPathname();
  const applicationId = basicAppConfig.applicationId;
  const collectionName = typesenseConfig?.[TypesenseCollectionName[Collection.CHARACTERS]] ?? "";
  const adapter = useCharactersAdapter(typesenseConfig?.[TypesenseApiKey[Collection.CHARACTERS]]);
  const isPartner = useMemo(() => getIsPartner(), []);
  const { shouldFilterNsfwCharacters } = useShouldFilterNsfwCharacters({
    isNsfwToggled: typedLocalStorageService.getItem(NSFW_TOGGLE_LOCALSTORAGE_KEY) === "true",
    isShowNsfwEnabled: showNSFW,
  });

  // As in the Discover tab: wait for a signed-in user's settings so the NSFW filter uses their real choice
  // (a signed-out user has no settings to wait for, and a failed fetch must not block the query).
  const shouldDelayQuery = isAuthenticated && !areUserSettingsLoaded && areUserSettingLoading !== FetchStatus.failed;

  const { stateResult, stateIsExpanded, stateIsLoadingMore, handleSeeAll, handleLoadMore } = useProfileBotsQuery({
    rulebookId,
    adapter,
    collectionName,
    applicationId,
    isEnabled: isAuthenticated && !shouldDelayQuery,
    shouldFilterNsfw: shouldFilterNsfwCharacters,
  });

  const pagePath = localizePathname("");
  const screenName = useMemo(() => getBusinessRouteScreen(pagePath) as Screen, [pagePath]);

  const characterCardActionsHandler = useCallback(
    (action: CharacterCardActions | CharacterCardDropdownActions, characterId: string) => {
      if (action === CharacterCardActions.FAVORITE) {
        void onUserLikebot(characterId, screenName).catch(() => {});
      }
    },
    [onUserLikebot, screenName],
  );

  const configContext: HitConfigContext = useMemo(
    () => ({
      likedBots,
      chatLanguage,
      localizePathname,
      t,
      getTranslatedTag,
      blurNSFW,
      isPartner,
      isAgeVerificationRequired,
      isAuthenticated,
      characterCardActionsHandler,
    }),
    [
      likedBots,
      chatLanguage,
      localizePathname,
      t,
      getTranslatedTag,
      blurNSFW,
      isPartner,
      isAgeVerificationRequired,
      isAuthenticated,
      characterCardActionsHandler,
    ],
  );

  const configs = useMemo<HitConfig[]>(() => {
    if (stateResult.status !== ProfileBotsStatus.READY) {
      return [];
    }
    return stateResult.hits.map((hit) => buildCardConfig(hit, configContext));
  }, [stateResult, configContext]);

  return {
    configs,
    found: stateResult.status === ProfileBotsStatus.READY ? stateResult.found : 0,
    isLoading: stateResult.status === ProfileBotsStatus.IDLE || stateResult.status === ProfileBotsStatus.LOADING,
    isHidden: stateResult.status === ProfileBotsStatus.ERROR,
    isExpanded: stateIsExpanded,
    isLoadingMore: stateIsLoadingMore,
    hasMore: stateResult.status === ProfileBotsStatus.READY && stateResult.hasMore,
    onSeeAll: handleSeeAll,
    onLoadMore: handleLoadMore,
  };
}
